import { Grid, Typography } from '@mui/material';
import { ChartCard } from '../chart-card';
import { RankBar } from '../charts';
import { ReportColumn, ReportTable, ShareCell } from '../report-table';
import { inr, num } from '../report-theme';
import { LineItemRow, rankedCategories, ReportModel } from '../report-data';

interface CatalogTabProps {
	report: ReportModel;
	kind: 'service' | 'product';
}

/**
 * Services and Products share a shape — a ranking plus the numbers behind it —
 * so they share a component and differ only in their source rows and wording.
 */
export const CatalogTab = ({ report, kind }: CatalogTabProps) => {
	const isService = kind === 'service';
	const noun = isService ? 'service' : 'product';
	const categories = rankedCategories(isService ? report.serviceCategories : report.productCategories);
	const revenue = isService ? report.totalServiceSales : report.totalProductSales;
	const chartRows = categories.slice(0, 10);
	const top = categories[0];

	const columns: ReportColumn<LineItemRow>[] = [
		{
			title: noun,
			render: (row) => (
				<Typography variant="body2" sx={{ fontWeight: 500 }}>
					{row.name}
				</Typography>
			),
		},
		{ title: 'Sold', align: 'right', render: (row) => num(row.quantity) },
		{ title: 'Avg price', align: 'right', render: (row) => inr(row.averagePrice) },
		{
			title: 'Revenue',
			align: 'right',
			render: (row) => (
				<Typography variant="body2" sx={{ fontWeight: 600 }}>
					{inr(row.revenue)}
				</Typography>
			),
		},
		{ title: 'Share', align: 'right', width: 140, render: (row) => <ShareCell share={row.share} /> },
	];

	return (
		<Grid container spacing={3}>
			<Grid item xs={12} lg={6}>
				<ChartCard
					title={`Revenue by ${noun}`}
					subtitle={
						top
							? `${top.name} leads with ${inr(top.revenue)} of ${inr(revenue)}`
							: `Top ${noun}s in the selected period`
					}
					isEmpty={chartRows.length === 0}
					emptyMessage={`No ${noun} revenue in the selected period.`}
				>
					<RankBar
						categories={chartRows.map((row) => row.name)}
						values={chartRows.map((row) => row.revenue)}
					/>
				</ChartCard>
			</Grid>

			<Grid item xs={12} lg={6}>
				<ChartCard
					title={`${isService ? 'Service' : 'Product'} breakdown`}
					subtitle="Times sold, average price actually charged, and share of takings"
				>
					<ReportTable
						columns={columns}
						rows={categories}
						emptyMessage={`No ${noun}s sold in the selected period.`}
						maxHeight={420}
					/>
				</ChartCard>
			</Grid>
		</Grid>
	);
};
